import {
  Component,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
  inject
} from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import {
  AwBadgeComponent,
  AwTableComponent,
  AwDividerComponent,
  AwButtonDirective,
  AwFooterComponent,
  AwSpinnerComponent,
  AwDialogComponent,
  DialogOptions,
  AwToastComponent,
  ToastVariants,
  TableRow,
  TableCellInput,
  TableCellTypes,
} from '@assetworks-llc/aw-component-lib';
import { AwDateFormComponent } from '../../../../shared/components/aw-date-form/aw-date-form.component';
import { ParentIntegrationService } from '../../../../core/services/parent-integration.service';
import {
  catchError,
  finalize,
  forkJoin,
  mergeMap,
  of,
} from 'rxjs';
import { EmployeeAuthenticationService } from '../../../../core/services/employee-authentication.service';
import { SecretService } from '../../../../core/services/secret.service';
import {
  UserService,
} from '../../../../core/services/user.service';
import { linkCellTemplate } from '../../../../shared/utils/link-cell-template';
import { TableActionBarComponent } from '../../../../shared/components/table-action-bar/table-action-bar.component';
import { injectIsPhone } from '../../../../shared/utils/breakpoints';
import { ResultsStatusComponent } from '../../../../shared/components/results-status/results-status.component';

@Component({
    selector: 'app-parent-integration-management',
    imports: [
        AwSpinnerComponent,
        AwToastComponent,
        AwDialogComponent,
        AwTableComponent,
        AwDividerComponent,
        CommonModule,
        AwDateFormComponent,
        AwButtonDirective,
        AwFooterComponent,
        ResultsStatusComponent,
        TableActionBarComponent,
    ],
    templateUrl: './parent-integration-management.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        './parent-integration-management.component.scss',
        '../../../../../styles/management.scss',
    ]
})
export class ParentIntegrationManagementComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly parentIntegrationService = inject(ParentIntegrationService);
  private readonly employeeAuthenticationService = inject(EmployeeAuthenticationService);
  private readonly secretService = inject(SecretService);
  private readonly userService = inject(UserService);

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;
  /** Below 768px the table drops its height cap and the page scrolls (see .table-page in management.scss). */
  readonly isPhone = injectIsPhone();
  /** The table's current text filter, re-applied when the table is re-created across the phone breakpoint. */
  private tableFilterText = '';
  awTableComponent!: AwTableComponent;

  @ViewChild(AwTableComponent)
  set awTable(table: AwTableComponent | undefined) {
    if (!table) {
      return;
    }
    this.awTableComponent = table;
    if (this.tableFilterText) {
      table.applyFilter(this.tableFilterText);
    }
  }

  private applyTableFilter(text: string): void {
    this.tableFilterText = text;
    this.awTableComponent?.applyFilter(text);
  }

  isLoading: boolean = false;

  tableData = signal<any[]>([]);
  tableDataCopy: any[] = [];
  enableStickyOptions = signal(true);
  sortDisabled = signal(false);

  parentIntegrationToDelete: TableRow | null = null;
  openDeleteDialog: boolean = false;
  userPermissions: Record<string, boolean> = {};

  openAddThirdParty(): void {
    this.router.navigate(['/parent-integration-management/add-parent-integration']);
  }

  dialogOptionsDelete: DialogOptions = {
    variant: 'alert',
    title: `Delete Third Party`,
    description: `By selecting Delete, you will permanently delete this information. Choose Cancel, to go back to the view.`,
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Delete',
  };

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.userPermissions = this.userService.getUserPermissions();

    if (this.userPermissions['canDelete']) {
      this.columnsDefinition.push({
        type: TableCellTypes.IconButton,
        key: 'deleteButton',
        label: 'Remove',
        buttonType: 'primary',
        isExpanded: false,
        icon: 'delete',
        action: (data: TableRow) => {
          this.openDeleteDialog = true;
          this.parentIntegrationToDelete = data;
        },
      });
    }

    this.parentIntegrationService
      .getAllParentIntegrations()
      .pipe(
        finalize(() => (this.isLoading = false)),
        catchError((error) => {
          this.buildToastMessage('Failed to load third parties', 'error');
          return of([]);
        })
      )
      .subscribe((parentIntegrations) => {
        const parentIntegrationData: any[] =
          parentIntegrations?.map((parentIntegration) => ({
            parentIntegrationInfo: {
              name: parentIntegration.name,
              id: parentIntegration.organizationId,
              integrationIds: parentIntegration.integrationIds,
              detailsPageRoute: `/parent-integration-management/parent-integration-details/${parentIntegration.organizationId}`,
            },
            statusBadge: {
              enabled: parentIntegration.enabled,
              color: parentIntegration.enabled
                ? 'success-green-700'
                : 'aw-dark-gray-1000',
              label: parentIntegration.enabled ? 'Active' : 'Disabled',
            },
            num_integrations: parentIntegration.integrationIds?.length ?? 0,
            modified_date: parentIntegration.modifiedOn
              ? new Date(parentIntegration.modifiedOn).toLocaleDateString()
              : 'N/A',
            date_created: parentIntegration.createdOn
              ? new Date(parentIntegration.createdOn).toLocaleDateString()
              : 'N/A',
          })) ?? [];
        this.tableData.set(parentIntegrationData);
        this.tableDataCopy = parentIntegrationData;
      });
  }

  columnsDefinition: TableCellInput[] = [
    {
      align: 'left',
      type: TableCellTypes.Custom,
      isExpanded: false,
      // CCL sorts a combined column by its key (table.mdx, 'custom columns that use combineFields'), so the key is the
      // displayed name; an object or missing field left sorting dead.
      key: 'parentIntegrationInfo.name',
      combineFields: [
        'parentIntegrationInfo.name',
        'parentIntegrationInfo.detailsPageRoute',
        'parentIntegrationInfo.id',
      ],
      label: 'Third-Party Name',
      combineTemplate: (values: any[]) => ({
        template: linkCellTemplate(values[0], values[1], values[2]),
      }),
      sort: true,
    },
    {
      type: TableCellTypes.Custom,
      isExpanded: false,
      key: 'Status',
      combineFields: ['statusBadge.color', 'statusBadge.label'],
      combineTemplate: (values: any[]) => ({
        component: AwBadgeComponent,
        componentData: {
          color: values[0],
          label: values[1],
        },
      }),
      align: 'center',
    },
    {
      type: TableCellTypes.Title,
      key: 'num_integrations',
      label: 'Number of Third-Party Integrations',
      isExpanded: false,
      sort: true,
      align: 'right',
    },
    {
      type: TableCellTypes.Title,
      key: 'modified_date',
      label: 'Last Modified',
      isExpanded: false,
      sort: true,
      align: 'center',
    },
    {
      type: TableCellTypes.Title,
      key: 'date_created',
      label: 'Created On',
      isExpanded: false,
      sort: true,
      align: 'right',
    },
  ];

  onDateSelected(date: Date | null) {
    if (date === null) {
      this.applyTableFilter('');
      return;
    }
    const filteredDate: string = date!.toLocaleDateString();
    this.applyTableFilter(filteredDate);
  }

  dialogFormDeleteHandler(formValues: any) {
    if (this.parentIntegrationToDelete) {
      const parentIntegrationId: string =
        this.parentIntegrationToDelete['parentIntegrationInfo'].id;

      this.isLoading = true;

      this.secretService
        .listAllSecrets({ organizationId: parentIntegrationId })
        .pipe(
          mergeMap((secrets) => {
            if (!secrets || secrets.length === 0) {
              return of([]);
            }
            return forkJoin(
              secrets.map((secret) =>
                this.secretService
                  .deleteSecret({
                    organizationId: parentIntegrationId,
                    secretName: secret.name || '',
                  })
                  .pipe(catchError(() => of(false)))
              )
            );
          }),
          catchError(() => of([])),
          mergeMap(() =>
            this.employeeAuthenticationService.deleteAuthenticationConfig({ 
              organizationId: parentIntegrationId 
            })
          ),
          catchError(() => of(false)),
          mergeMap(() =>
            this.parentIntegrationService.deleteParentIntegration({
              organizationId: parentIntegrationId,
            })
          ),
          finalize(() => {
            this.isLoading = false;
            this.openDeleteDialog = false;
            this.parentIntegrationToDelete = null;
          })
        )
        .subscribe({
          next: (deleted) => {
            const filtered = this.tableDataCopy.filter(
              (row) => row['parentIntegrationInfo'].id !== parentIntegrationId
            );
            this.tableData.set(filtered);
            this.tableDataCopy = [...filtered];
            this.buildToastMessage(
              'Successfully deleted third party',
              'success'
            );
          },
          error: () => {
            this.buildToastMessage('Failed to delete third party', 'error');
          },
        });
    }
  }

  dialogCloseHandler() {
    this.openDeleteDialog = false;
  }
}
